/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import JSZip from 'jszip';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import chunk from '@linc-technologies/linced-common/utils/chunk';
import mapWithConcurrency from '@linc-technologies/linced-common/utils/map-with-concurrency';
import reportStudentName from '@linc-technologies/linced-common/utils/report-student-name';
import { guidFor } from '@ember/object/internals';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isiOS } from '@linc-technologies/linced-common/utils/platform';
import { scheduleOnce } from '@ember/runloop';
import { tagName } from '@ember-decorators/component';

// Cap on simultaneous preload requests. The browser allows ~6 connections per
// origin; draining the preload fan-out through a pool of this size keeps the
// request queue from spiking at the start of a download.
const PRELOAD_CONCURRENCY = 6;

// Max person IDs per batched progression query. The IDs are UUIDs serialised
// into the GET query string, so an unbounded batch could exceed a proxy's
// ~8KB URL limit (414). 50 keeps the URL well under that while still collapsing
// the per-(subject × student) fan-out to ceil(students / 50) per subject.
const PROGRESSION_PERSON_CHUNK = 50;

@tagName('')
export default class BulkPdfDownload extends Component {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @tracked
  isiOS = false;

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.isiOS = isiOS();
  }

  init() {
    super.init(...arguments);
    this._captureContainerId = `bulk-pdf-capture-${guidFor(this)}`;
  }

  // @reportTemplate — the report template record (used by ReportView for rendering)
  // @studentReports — array of student report records to download (pre-filtered by caller)
  // @student — optional pre-resolved student (person) record; used when studentId is not set on the report
  // @reportName — base name for the ZIP file (no extension)
  // @contextName — group name or learner name appended to the ZIP filename
  // @disabled — whether the button should be disabled
  // @buttonClass — CSS classes for the button
  // @buttonStyle — inline style string for the button

  @tracked
  isGenerating = false;

  @tracked
  _currentStudent = null;
  @tracked
  _currentStudentReport = null;
  @tracked
  _currentTemplate = null;
  @tracked
  _progress = 0;
  @tracked
  _total = 0;
  @tracked
  _waitForDataReady = null;

  // `windowWidth` + `onclone` fix the >100% browser zoom case where
  // html2canvas's iframe reads a wrong scrollHeight and clips the report
  // bottom. Don't set width/height — the per-page split must align with
  // pagebreaks.js's calibration (canvas.width === inner.px.width * scale).
  _html2pdfOptions(filename, el) {
    return {
      margin: [8, 8, 8, 8],
      filename,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollY: 0,
        logging: false,
        windowWidth: el ? el.offsetWidth : undefined,
        onclone: el
          ? (_clonedDoc, clonedEl) => {
              clonedEl.style.height = clonedEl.scrollHeight + 'px';
            }
          : undefined,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      // Avoid list: selectors html2pdf treats as atomic. Notable
      // omissions:
      //   - md-card / .preview-pv / .preview-assessment / .preview-
      //     content-header / .preview-narrative-header / school-bar /
      //     preview-top / progress-header / strand-slider-visual SVG:
      //     each is wrapped at capture time (see `_withReportPdfEl`).
      //     The WRAPPER class is what's listed here; listing the inner
      //     banner alongside defeats the 10px transparent buffer that
      //     absorbs sub-pixel bleed.
      //   - bare `strong`/`b`: capture-time pass tags only the sub-
      //     heading shape (sole content of its block parent) with
      //     `.preview-pdf-heading-strong`; inline mid-sentence bolds
      //     stay untouched.
      // pv-header-row + pv-row are listed so multi-page progress
      // visuals split between rows instead of mid-row when html2pdf
      // disables the avoid push (nPages > 1).
      pagebreak: {
        mode: ['css', 'legacy'],
        avoid: [
          '.report-builder__preview-section-head-group',
          '.report-builder__preview-narrative-head-group',
          '.report-builder__preview-banner-buffer',
          '.report-builder__preview-strand-card-keep',
          'p',
          '.report-builder__preview-attendance-chart',
          '.report-builder__preview-attendance-legend',
          '.report-builder__preview-attendance-legend-item',
          '.report-builder__preview-attendance-table',
          '.report-builder__preview-pv-chart',
          '.report-builder__preview-pv-scroll',
          '.report-builder__preview-pv-header-row',
          '.report-builder__preview-pv-row',
          '.report-builder__preview-progress-line',
          '.report-builder-progress-table',
          '.report-builder__preview-strands-table-wrap',
          '.report-builder__preview-strands-table',
          '.report-builder__preview-strand-row',
          '.report-builder__preview-strand-bar',
          '.report-builder__preview-strand-bar-wrap',
          'li',
          'tr',
          'thead',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          '.report-builder__preview-pdf-heading-strong',
        ],
      },
    };
  }

  // Tile a "NOT FINAL" watermark across the report element so a downloaded
  // draft can't be mistaken for the published version. Returns a cleanup fn.
  // Only applied to reports still in review — published/ready-to-publish
  // downloads (group index, student view, caregiver app) are left unmarked.
  addReviewWatermark(el) {
    const report = this._currentStudentReport;
    if (!report || report.get('state') !== StudentReportState.READY_TO_REVIEW) {
      return () => {};
    }

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: '1000',
    });

    // Repeat the mark down the full (multi-page) height of the report.
    const height = el.scrollHeight || el.offsetHeight;
    const spacing = 360;
    const count = Math.max(1, Math.ceil(height / spacing));
    for (let i = 0; i < count; i++) {
      const mark = document.createElement('div');
      mark.textContent = 'NOT FINAL';
      Object.assign(mark.style, {
        position: 'absolute',
        top: `${i * spacing + spacing / 2}px`,
        left: '50%',
        transform: 'translateX(-50%) rotate(-30deg)',
        fontSize: '72px',
        fontWeight: '700',
        letterSpacing: '8px',
        whiteSpace: 'nowrap',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: 'rgba(220, 38, 38, 0.13)',
      });
      overlay.appendChild(mark);
    }

    el.appendChild(overlay);
    return () => overlay.remove();
  }

  async _withReportPdfEl(fn) {
    const el = document.querySelector(
      `#${this._captureContainerId} .report-builder__preview`,
    );
    if (!el) {
      return;
    }

    const origStyle = el.getAttribute('style') || '';

    // Restore registries declared up front so the `finally` block can
    // drain them even if a mid-setup pass throws.
    const headGroupRestoreFns = [];
    const bannerBufferRestoreFns = [];
    const strandCardKeepRestoreFns = [];
    const headingStrongRestoreFns = [];
    const cardRestoreFns = [];
    let pdfStyle = null;
    let removeWatermark = () => {};

    try {
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
      el.style.overflowY = 'visible';
      el.style.position = 'relative';
      el.style.top = 'auto';
      el.style.height = 'auto';

      pdfStyle = document.createElement('style');
      // PDF-only style overrides. Side-by-side narrative columns stack
      // because html2pdf v0.14 can't split flex columns cleanly across
      // pages. The 10px transparent buffer at the top of every wrap
      // class absorbs sub-pixel rounding + html2canvas SVG anti-
      // aliasing slop. --orphan-guard extends the wrapper's avoid
      // footprint down by 50px (via negative margin to keep visual
      // layout unchanged) so a header pushes with the first row of
      // its body.
      pdfStyle.textContent = `
        .html2pdf__container md-card {
          box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.21) !important;
        }
        .html2pdf__container .report-builder__preview-narrative-header,
        .html2pdf__container .report-builder__preview-pv {
          margin-top: 4px !important;
        }
        .html2pdf__container .report-builder__preview-component-row {
          flex-direction: column !important;
        }
        /* Restore the 16px padding the head-group wrap yanked off the
           non-header child when it pulled it out of md-card-content. */
        .html2pdf__container .report-builder__preview-section-head-group
          > :not(.report-builder__preview-content-header) {
          padding-left: 16px;
          padding-right: 16px;
          padding-top: 16px;
        }
        .html2pdf__container .report-builder__preview-section-head-group,
        .html2pdf__container .report-builder__preview-narrative-head-group {
          overflow: hidden;
          padding-top: 10px;
        }
        .html2pdf__container .report-builder__preview-banner-buffer {
          padding-top: 10px !important;
          overflow: hidden !important;
        }
        .html2pdf__container .report-builder__preview-banner-buffer--orphan-guard {
          padding-bottom: 50px !important;
          margin-bottom: -50px !important;
        }
        .html2pdf__container .report-builder__preview-strand-card-keep {
          padding-top: 10px !important;
          overflow: hidden !important;
        }
        /* Pin header line-heights: default \`line-height: normal\` let
           html2canvas at scale: 2 paint the meta line on top of a
           wrapped second line of subtext at high browser zoom (long
           template names wrap and the "Year 11" meta overlapped the
           wrap). Scoped here so it doesn't affect on-screen review /
           customise / caregiver report views. */
        .html2pdf__container .report-builder__preview-student-name {
          line-height: 1.3;
        }
        .html2pdf__container .report-builder__preview-student-subtext {
          line-height: 1.35;
        }
        .html2pdf__container .report-builder__preview-student-meta {
          line-height: 1.4;
        }
      `;
      document.head.appendChild(pdfStyle);

      // Orphan-header protection: wrap header + first body sibling
      // into a head-group div that's in the avoid list, so html2pdf
      // pushes the pair as a unit. Skip when the body is too tall —
      // the welded pair would be too big to push and we'd get wasted
      // whitespace instead. Thresholds differ because narrative
      // firstBody is the wrapper around the WHOLE narrative (often
      // 4+ paragraphs or a full list), so only short single-paragraph
      // narratives qualify; section firstBody is typically a single
      // component-row.
      const SECTION_HEAD_GROUP_MAX_BODY_PX = 200;
      const NARRATIVE_HEAD_GROUP_MAX_BODY_PX = 80;

      const wrapHeadGroup = (header, sibling, wrapperClass) => {
        if (!header || !sibling) return;
        const wrapper = document.createElement('div');
        wrapper.className = wrapperClass;
        const headerParent = header.parentNode;
        const headerNext = header.nextSibling;
        const siblingParent = sibling.parentNode;
        const siblingNext = sibling.nextSibling;

        headerParent.insertBefore(wrapper, header);
        wrapper.appendChild(header);
        wrapper.appendChild(sibling);

        headGroupRestoreFns.push(() => {
          siblingParent.insertBefore(sibling, siblingNext);
          headerParent.insertBefore(header, headerNext);
          wrapper.remove();
        });
      };

      // Section-level: content-header + first child of md-card-content.
      // Zero the body's padding-top during capture so yanking firstBody
      // out doesn't leave a 16px gap above the new first row.
      el.querySelectorAll('.report-builder__preview-content-header').forEach(
        (header) => {
          let body = header.nextElementSibling;
          while (body && body.tagName.toLowerCase() !== 'md-card-content') {
            body = body.nextElementSibling;
          }
          const firstBody = body && body.firstElementChild;
          if (!firstBody) return;
          if (firstBody.offsetHeight > SECTION_HEAD_GROUP_MAX_BODY_PX) return;
          const bodyOrigPadTop = body.style.paddingTop;
          body.style.paddingTop = '0px';
          wrapHeadGroup(
            header,
            firstBody,
            'report-builder__preview-section-head-group',
          );
          headGroupRestoreFns.push(() => {
            body.style.paddingTop = bodyOrigPadTop;
          });
        },
      );

      // Narrative-level: narrative-header + next sibling inside narrative-card.
      el.querySelectorAll('.report-builder__preview-narrative-header').forEach(
        (header) => {
          const next = header.nextElementSibling;
          if (!next) return;
          if (next.offsetHeight > NARRATIVE_HEAD_GROUP_MAX_BODY_PX) return;
          wrapHeadGroup(
            header,
            next,
            'report-builder__preview-narrative-head-group',
          );
        },
      );

      // Banner-buffer wrap: wrap each bleed-prone banner in a
      // transparent buffer div (10px padding-top + overflow:hidden, see
      // pdfStyle). The buffer is what html2pdf's avoid tracks; the
      // visible banner sits 10px below it, clear of sub-pixel rounding
      // and html2canvas SVG anti-aliasing. .preview-pv and
      // .preview-assessment are wrapped too because their container /
      // card backgrounds bleed the same way. orphanGuardSelectors get
      // the --orphan-guard modifier (extends avoid footprint downward
      // by 50px) — content-header and narrative-header are followed by
      // an independently-avoid-pushed body, so the guard keeps them
      // travelling together. Skip if already inside a head-group /
      // banner-buffer.
      const bufferWrapSelectors = [
        '.report-builder__preview-top',
        '.report-builder__preview-school-bar',
        '.report-builder__preview-content-header',
        '.report-builder__preview-narrative-header',
        '.report-builder__preview-progress-header',
        '.strand-slider-visual',
        '.report-builder__preview-pv',
        '.report-builder__preview-assessment',
      ];
      const orphanGuardSelectors = new Set([
        '.report-builder__preview-content-header',
        '.report-builder__preview-narrative-header',
      ]);
      bufferWrapSelectors.forEach((selector) => {
        el.querySelectorAll(selector).forEach((banner) => {
          const parent = banner.parentElement;
          if (
            parent &&
            (parent.classList.contains(
              'report-builder__preview-section-head-group',
            ) ||
              parent.classList.contains(
                'report-builder__preview-narrative-head-group',
              ) ||
              parent.classList.contains(
                'report-builder__preview-banner-buffer',
              ))
          ) {
            return;
          }
          const wrapper = document.createElement('div');
          wrapper.className = 'report-builder__preview-banner-buffer';
          if (orphanGuardSelectors.has(selector)) {
            wrapper.classList.add(
              'report-builder__preview-banner-buffer--orphan-guard',
            );
          }
          banner.parentNode.insertBefore(wrapper, banner);
          wrapper.appendChild(banner);
          bannerBufferRestoreFns.push(() => {
            wrapper.parentNode.replaceChild(banner, wrapper);
          });
        });
      });

      // Strand-table buffer: 3-row strand tables had a top-border
      // bleed even with the table itself in avoid. Wrap each
      // .strands-table-wrap directly (not the enclosing comp-target,
      // which only has its class in the write/review screen and not in
      // published downloads). Skip > 1 page units — html2pdf bypasses
      // avoid for those, and we fall back to the per-row avoid.
      const STRAND_CARD_KEEP_MAX_PX = 380;
      el.querySelectorAll(
        '.report-builder__preview-strands-table-wrap',
      ).forEach((inner) => {
        if (
          inner.parentElement &&
          inner.parentElement.classList.contains(
            'report-builder__preview-strand-card-keep',
          )
        ) {
          return;
        }
        if (inner.offsetHeight > STRAND_CARD_KEEP_MAX_PX) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'report-builder__preview-strand-card-keep';
        inner.parentNode.insertBefore(wrapper, inner);
        wrapper.appendChild(inner);
        strandCardKeepRestoreFns.push(() => {
          wrapper.parentNode.replaceChild(inner, wrapper);
        });
      });

      // Sub-heading tagging for inline <strong>/<b>. TinyMCE produces
      // both mid-sentence bolds ("was <strong>excellent</strong>") and
      // sub-heading bolds ("<p><strong>Proficient:</strong></p>"), so
      // we can't avoid the bare tag without cutting sentences. Tag
      // only the sub-heading shape: a strong/b that is the SOLE
      // element child of its block parent AND whose trimmed text
      // matches the parent's. The text check rules out "<p>Note:
      // <strong>X</strong></p>" (text-node sibling).
      el.querySelectorAll('p > strong, p > b, div > strong, div > b').forEach(
        (heading) => {
          const parent = heading.parentElement;
          if (!parent) return;
          if (parent.children.length !== 1) return;
          if (
            (parent.textContent || '').trim() !==
            (heading.textContent || '').trim()
          ) {
            return;
          }
          heading.classList.add('report-builder__preview-pdf-heading-strong');
          headingStrongRestoreFns.push(() => {
            heading.classList.remove(
              'report-builder__preview-pdf-heading-strong',
            );
          });
        },
      );

      // Move overflow clip to direct children so html2canvas renders md-card's box-shadow
      // while still clipping coloured headers to the card's border-radius.
      Array.from(el.querySelectorAll('md-card')).forEach((card) => {
        const r = getComputedStyle(card).borderRadius.split(' ')[0];
        const origCardOverflow = card.style.overflow;
        card.style.overflow = 'visible';

        const children = Array.from(card.children);
        const childRestoreData = children.map((child, i) => {
          const origOverflow = child.style.overflow;
          const origBorderRadius = child.style.borderRadius;
          const isFirst = i === 0;
          const isLast = i === children.length - 1;
          child.style.overflow = 'hidden';
          if (isFirst && isLast) {
            child.style.borderRadius = r;
          } else if (isFirst) {
            child.style.borderRadius = `${r} ${r} 0 0`;
          } else if (isLast) {
            child.style.borderRadius = `0 0 ${r} ${r}`;
          }
          return { child, origOverflow, origBorderRadius };
        });

        cardRestoreFns.push(() => {
          card.style.overflow = origCardOverflow;
          childRestoreData.forEach(
            ({ child, origOverflow, origBorderRadius }) => {
              child.style.overflow = origOverflow;
              child.style.borderRadius = origBorderRadius;
            },
          );
        });
      });

      // Added last (after style/height settle) so it tiles the full content
      // height and overlays everything beneath it.
      removeWatermark = this.addReviewWatermark(el);

      return await fn(el);
    } finally {
      // Restore order matters: unwind from outermost wrap inward.
      // strand-card-keep wraps may CONTAIN banner-buffer wraps;
      // head-group's saved siblingNext refs point to siblings that
      // banner-buffer may have moved. Heading-strong is class-only
      // and order-independent. Each step is isolated so a single
      // failure (e.g. wrapper.parentNode nulled by an external DOM
      // mutation mid-capture) doesn't skip the rest of the drain and
      // leave stray wrapper divs / pdfStyle behind.
      const safe = (label, fn) => {
        try {
          fn();
        } catch (e) {
          console.warn(`bulk-pdf: ${label} restore failed`, e);
        }
      };
      const drain = (label, fns) =>
        fns.forEach((restore, i) => safe(`${label}[${i}]`, restore));

      safe('watermark', removeWatermark);
      safe('origStyle', () => el.setAttribute('style', origStyle));
      drain('card', cardRestoreFns);
      drain('headingStrong', headingStrongRestoreFns);
      drain('strandCardKeep', strandCardKeepRestoreFns);
      drain('bannerBuffer', bannerBufferRestoreFns);
      drain('headGroup', headGroupRestoreFns);
      if (pdfStyle) {
        safe('pdfStyle', () => pdfStyle.remove());
      }
    }
  }

  @action
  registerWaitForReady(fn) {
    this._waitForDataReady = fn;
  }

  @action
  async downloadAll() {
    if (this.isGenerating || this.disabled) {
      return;
    }

    let reports = this.studentReports;

    if (
      (!reports || !reports.length) &&
      (!this.reportIds || !this.reportIds.length)
    ) {
      return;
    }

    this.isGenerating = true;
    this._progress = 0;
    this._total = 0;
    this._currentStudent = null;
    this._currentStudentReport = null;
    this._currentTemplate = null;

    try {
      // html2pdf.js is large. Load it only when a download starts.
      const { default: html2pdf } = await import('html2pdf.js');

      // Fetch reports on-demand when only IDs were passed in (keeps
      // the host route from eagerly populating the store).
      if (
        (!reports || !reports.length) &&
        this.reportIds &&
        this.reportIds.length
      ) {
        reports = (
          await mapWithConcurrency(
            this.reportIds.map(
              (id) => () =>
                this.store
                  .findRecord('reports/student-report', id)
                  .catch(() => null),
            ),
            PRELOAD_CONCURRENCY,
          )
        ).filter(Boolean);
      }

      if (!reports || !reports.length) {
        return;
      }
      // Preload sections per report. Per-report catch: in some app
      // contexts (e.g. caregiver) the API doesn't support the
      // standalone section query, but sections are sideloaded.
      await mapWithConcurrency(
        reports.map(
          (r) => () =>
            this.store
              .query('reports/student-report-section', {
                studentReportId: r.get('id'),
              })
              .catch(() => []),
        ),
        PRELOAD_CONCURRENCY,
      );

      // Resolve students from the report's studentId, or fall back
      // to @student when the report omits student_id (caregiver app).
      const students = this.student
        ? [this.student]
        : (
            await mapWithConcurrency(
              reports.map(
                (r) => () =>
                  this.store
                    .findRecord('person', r.belongsTo('studentId').id())
                    .catch(() => null),
              ),
              PRELOAD_CONCURRENCY,
            )
          ).filter(Boolean);

      this._total = students.length;

      // Reports may span multiple template versions (group index
      // bundles published reports across the version history).
      // Rendering with the wrong template makes
      // studentSectionMap lookups miss and strand visuals render
      // empty / show sample data, so resolve each report's own
      // template and ensure its sections are loaded.
      const uniqueTemplateIds = new Set();
      reports.forEach((r) => {
        const tid = r.belongsTo('reportTemplateId').id();
        if (tid) {
          uniqueTemplateIds.add(tid);
        }
      });

      await mapWithConcurrency(
        Array.from(uniqueTemplateIds).map((tid) => () => {
          const hasSections = this.store
            .peekAll('reports/report-template-section')
            .some((s) => s.belongsTo('reportTemplateId').id() === tid);
          if (hasSections) {
            return Promise.resolve();
          }
          // `reload: true` is required — the sideloaded template
          // arrives without sections, and findRecord short-circuits
          // on the cached record, ignoring `includeSections`.
          return this.store
            .findRecord('reports/report-template', tid, {
              reload: true,
              adapterOptions: { includeSections: true },
            })
            .catch(() => null);
        }),
        PRELOAD_CONCURRENCY,
      );

      const templateByReportId = {};
      reports.forEach((r) => {
        const tid = r.belongsTo('reportTemplateId').id();
        templateByReportId[r.get('id')] =
          this.store.peekRecord('reports/report-template', tid) ||
          this.reportTemplate;
      });

      // Collect subjectIds + assessment-data presence across all
      // template versions we're rendering.
      const subjectIds = new Set();
      let hasAssessmentData = false;
      uniqueTemplateIds.forEach((tid) => {
        this.store
          .peekAll('reports/report-template-section')
          .filter((s) => s.belongsTo('reportTemplateId').id() === tid)
          .forEach((section) => {
            const config = section.get('configuration');
            if (!config) {
              return;
            }
            try {
              const parsed =
                typeof config === 'string' ? JSON.parse(config) : config;
              if (parsed && parsed.subjectId) {
                subjectIds.add(parsed.subjectId);
              }
              if (
                parsed &&
                (parsed.components || []).some(
                  (c) => c.type === 'assessment-data',
                )
              ) {
                hasAssessmentData = true;
              }
            } catch (e) {
              // malformed configuration — skip
            }
          });
      });

      // Per-subject preloads matching the view route's pattern.
      // Progressions / levels / expectations must be queried with
      // `goalSubjectId` (and `type`, not `progressionType`) to match
      // what report-progress-visual / strand components peek for;
      // global findAll returns a different slice and leaves charts
      // blank. Preloads are thunks, drained through a bounded pool
      // (PRELOAD_CONCURRENCY).
      const schoolId = students[0] && students[0].get('school_id');
      const personIds = students.map((s) => s.get('personId') || s.get('id'));
      const personIdChunks = chunk(personIds, PROGRESSION_PERSON_CHUNK);
      // milestone-set is awaited here: the reporting route fires it
      // but doesn't await, so the first download otherwise renders
      // charts with empty lines (progressions filter on milestoneIds).
      const preloads = [
        () =>
          this.store
            .query('milestones/milestone-set', { include: 'milestones' })
            .catch(() => null),
      ];
      if (schoolId) {
        preloads.push(() =>
          this.store.findRecord('school', schoolId).catch(() => null),
        );
      }
      // Preload assessment data so report-view can render grades without
      // flashing "Loading assessment data..." during PDF capture.
      if (hasAssessmentData) {
        preloads.push(
          () => this.store.findAll('assessments/assessment').catch(() => null),
          () => this.store.findAll('assessments/module').catch(() => null),
        );
        students.forEach((student) => {
          const personId = student.get('personId') || student.get('id');
          preloads.push(() =>
            this.store
              .query('assessments/grade', { personId, latest: true })
              .catch(() => null),
          );
        });
      }
      subjectIds.forEach((subjectId) => {
        preloads.push(() =>
          this.store.findRecord('goals/subject', subjectId).catch(() => null),
        );
        preloads.push(() =>
          this.store
            .query('milestones/level', { goalSubjectId: subjectId })
            .catch(() => null),
        );
        preloads.push(() =>
          this.store
            .query('milestones/expectation', { goalSubjectId: subjectId })
            .catch(() => null),
        );
        // Batched per (subject × chunk of students) — progression
        // accepts a personIds list and groups server-side. Chunked
        // to keep the GET URL under the proxy limit.
        personIdChunks.forEach((idsChunk) => {
          preloads.push(() =>
            this.store
              .query('milestones/progression', {
                goalSubjectId: subjectId,
                personIds: JSON.stringify(idsChunk),
                type: ProgressionType.PROGRESSION,
                latest: true,
              })
              .catch(() => null),
          );
        });
      });
      await mapWithConcurrency(preloads, PRELOAD_CONCURRENCY);

      const reportByStudentId = {};
      reports.forEach((r) => {
        reportByStudentId[r.belongsTo('studentId').id()] = r;
      });

      // @student-driven hosts map directly to reports[0]; group
      // hosts map by report.studentId (nullable in caregiver app).
      const resolveReportForStudent = this.student
        ? () => reports[0]
        : (student) => reportByStudentId[student.get('id')];

      const zip = new JSZip();
      // Dedupe filenames so two students with the same name don't
      // silently overwrite each other via JSZip.file().
      const usedFilenames = new Set();
      const dedupeFilename = (base) => {
        if (!usedFilenames.has(base)) {
          usedFilenames.add(base);
          return base;
        }
        const extIdx = base.lastIndexOf('.');
        const stem = extIdx > 0 ? base.slice(0, extIdx) : base;
        const ext = extIdx > 0 ? base.slice(extIdx) : '';
        let i = 2;
        let candidate = `${stem} (${i})${ext}`;
        while (usedFilenames.has(candidate)) {
          i += 1;
          candidate = `${stem} (${i})${ext}`;
        }
        usedFilenames.add(candidate);
        return candidate;
      };

      for (const student of students) {
        this.incrementProperty('_progress');
        const currentReport = resolveReportForStudent(student);
        this._currentStudent = student;
        this._currentStudentReport = currentReport;

        this._currentTemplate =
          (currentReport && templateByReportId[currentReport.get('id')]) ||
          this.reportTemplate;

        await new Promise((resolve) =>
          scheduleOnce('afterRender', null, resolve),
        );
        if (this._waitForDataReady) {
          await this._waitForDataReady();
        }
        // Double rAF ensures ChartResizer (ResizeObserver) has received
        // its final dimensions and rendered charts before capture.
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve)),
        );

        // Name the file as the report names the student.
        const studentName =
          reportStudentName(student, this._currentTemplate).fullName ||
          student.get('fullName');
        const filename = dedupeFilename(
          `${studentName} ${this.reportName || 'Report'}.pdf`,
        );
        let blob;
        try {
          blob = await this._withReportPdfEl((el) =>
            html2pdf()
              .set(this._html2pdfOptions(filename, el))
              .from(el)
              .outputPdf('blob'),
          );
        } catch (e) {
          console.warn(`Failed to generate PDF for ${studentName}`, e);
        } finally {
          // html2pdf appends a fixed full-screen `.html2pdf__overlay`
          // in toContainer() and only removes it on successful
          // toCanvas. If html2canvas (or the `onclone` hook) throws,
          // the invisible overlay stays attached and silently eats
          // every click on the page. Sweep any stray overlays here.
          document
            .querySelectorAll('.html2pdf__overlay')
            .forEach((n) => n.remove());
        }

        if (blob) {
          zip.file(filename, blob);
        }
      }

      this._currentStudent = null;
      this._currentStudentReport = null;
      this._currentTemplate = null;

      const addedFiles = Object.keys(zip.files).length;
      if (addedFiles === 0) {
        this.paperToaster.show('Failed to generate PDFs. Please try again.', {
          duration: 8000,
          position: 'top right',
          toastClass: 'warning',
        });
        return;
      }

      if (addedFiles < students.length) {
        this.paperToaster.show(
          `${
            students.length - addedFiles
          } PDF(s) failed to generate and were excluded from the download.`,
          { duration: 8000, position: 'top right', toastClass: 'warning' },
        );
      }

      // Bare-PDF shortcut for single-student intent (review screen,
      // caregiver app, or a group selection that resolved to one).
      // Gate on students.length, NOT addedFiles — otherwise a 9-of-
      // 10 partial failure in a class download arrives as a bare PDF
      // + a failure toast, hiding the degraded outcome.
      if (students.length === 1 && addedFiles === 1) {
        const [[filename, zipEntry]] = Object.entries(zip.files);
        const pdfBlob = await zipEntry.async('blob');
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      const zipBaseName = this.contextName
        ? `${this.contextName} ${this.reportName || 'Reports'}`
        : this.reportName || 'Reports';
      a.download = `${zipBaseName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[BulkPdfDownload] Download failed:', e);
      this.paperToaster.show('Failed to generate PDF. Please try again.', {
        duration: 8000,
        position: 'top right',
        toastClass: 'warning',
      });
    } finally {
      this.isGenerating = false;
      this._currentStudent = null;
      this._currentStudentReport = null;
      this._currentTemplate = null;
    }
  }
}
